import React, { useEffect, useState } from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Chip
} from '@mui/material'
import PublicIcon from '@mui/icons-material/Public'
import { Api } from '../helpers'
import { countries, getCurrency } from '../helpers/countries'

interface Charge {
  Percentage: number
  Max: number | null
  Min: number
}

interface StateProps {
  charges: Charge[]
  loading: boolean
  selectedCountry: string
}

const Pricing = () => {
  const [state, setState] = useState<StateProps>({
    charges: [],
    loading: true,
    selectedCountry: 'Kenya'
  })
  const { charges, loading, selectedCountry } = state

  const getFeeCharges = (country: string) => {
    setState(s => ({ ...s, loading: true }))
    Api({
      controller: 'Payments',
      action: 'getFeeCharges',
      country
    })
      .then((resp: any) => {
        setState(s => ({ ...s, charges: resp, loading: false }))
      })
      .catch(() => setState(s => ({ ...s, loading: false })))
  }

  useEffect(() => {
    getFeeCharges(selectedCountry)
  }, [])

  const currency = getCurrency(selectedCountry)
  const selectedCountryData = countries.find(c => c.name === selectedCountry)

  // A worked example, computed from the fetched brackets themselves (not hardcoded) so it can
  // never drift out of sync with the table above - picks the entry point of the top bracket
  // (the last row's Min) since that's the smallest amount that actually crosses every band below
  // it, making the "marginal, not flat" point as concretely as possible.
  const exampleAmount = charges.length ? charges[charges.length - 1].Min : null
  const exampleFee = exampleAmount === null ? null : charges.reduce((fee, { Min, Max, Percentage }) => {
    if (exampleAmount <= Min - 1) return fee
    const bandAmount = Math.min(exampleAmount, Max ?? Infinity) - (Min - 1)
    return fee + bandAmount * Percentage
  }, 0)
  const topRate = charges.length ? charges[charges.length - 1].Percentage : 0

  return (
    <Box
      id='pricing'
      sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 10 },
        bgcolor: '#f9fafb'
      }}
    >
      {/* Section Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
        <Typography
          data-aos="fade-down"
          sx={{ color: 'primary.main', fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', mb: 1 }}
        >
          Transparent Pricing
        </Typography>
        <Typography
          variant='h3'
          data-aos="fade-up"
          sx={{ fontWeight: 800, color: '#0d1117', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 2 }}
        >
          Simple, Fair Charges
        </Typography>
        <Typography
          data-aos="fade-up"
          data-aos-delay="100"
          sx={{ color: '#6b7280', maxWidth: 560, mx: 'auto', fontSize: '1rem', lineHeight: 1.8 }}
        >
          An installation fee applies, refundable upon termination. Transaction fees are marginal, like a tax
          bracket - each rate below only applies to the portion of a game's revenue that falls within that band,
          not the whole amount.
        </Typography>
      </Box>

      {/* Country Selector */}
      <Box
        data-aos="fade-up"
        data-aos-delay="150"
        sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}
      >
        <FormControl size='small' sx={{ minWidth: 260 }}>
          <InputLabel id='country-select-label' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PublicIcon sx={{ fontSize: 16 }} /> Country
          </InputLabel>
          <Select
            labelId='country-select-label'
            value={selectedCountry}
            label='Country'
            onChange={e => {
              const country = e.target.value
              setState(s => ({ ...s, selectedCountry: country }))
              getFeeCharges(country)
            }}
            sx={{ borderRadius: 2, bgcolor: '#fff' }}
            renderValue={val => {
              const c = countries.find(x => x.name === val)
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{c?.flag}</span>
                  <span>{val}</span>
                  <Typography component='span' sx={{ color: '#9ca3af', fontSize: 13, ml: 0.5 }}>({c?.currency})</Typography>
                </Box>
              )
            }}
          >
            {countries.map(c => (
              <MenuItem key={c.code} value={c.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <span>{c.flag}</span>
                  <span>{c.name}</span>
                  <Typography component='span' sx={{ color: '#9ca3af', fontSize: 12, ml: 'auto' }}>{c.currency}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Box data-aos="fade-up" data-aos-delay="200" sx={{ maxWidth: 680, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
          }}
        >
          <Box sx={{ bgcolor: '#0d1117', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
              Pool Owner Transaction Fees
            </Typography>
            {selectedCountryData && (
              <Chip
                label={`${selectedCountryData.flag} ${selectedCountryData.name} · ${currency}`}
                size='small'
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#d1d5db', fontSize: 12, border: '1px solid rgba(255,255,255,0.15)' }}
              />
            )}
          </Box>

          {loading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            </Box>
          ) : charges.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ color: '#9ca3af', fontSize: 14 }}>No charges available for this country</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Min Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Max Amount
                    </TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700, color: '#374151', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Fee Rate
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {charges.map(({ Max, Min, Percentage }, i) => (
                    <TableRow
                      key={i}
                      sx={{
                        '&:last-child td': { border: 0 },
                        '&:hover': { bgcolor: '#f9fafb' },
                        transition: 'background 0.15s'
                      }}
                    >
                      <TableCell sx={{ color: '#111827', fontSize: '0.95rem' }}>
                        {currency} {Min.toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: '#111827', fontSize: '0.95rem' }}>
                        {Max === null ? 'and above' : `${currency} ${Max.toLocaleString()}`}
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={`${(Percentage * 100).toFixed(1)}%`}
                          size='small'
                          sx={{
                            bgcolor: 'primary.main',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            minWidth: 64
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {exampleAmount !== null && exampleFee !== null && (
          <Box
            data-aos="fade-up"
            data-aos-delay="250"
            sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: '#eef2ff', border: '1px solid #c7d2fe' }}
          >
            <Typography sx={{ color: '#3730a3', fontSize: '0.85rem', lineHeight: 1.7 }}>
              <strong>Worked example:</strong> a {currency} {exampleAmount.toLocaleString()} game isn't charged a
              flat {(topRate * 100).toFixed(1)}% - it's each row's rate applied only to the slice of the amount in
              that row, adding up to {currency} {exampleFee.toFixed(2)} total
              ({((exampleFee / exampleAmount) * 100).toFixed(1)}% effective).
            </Typography>
          </Box>
        )}

        <Typography
          data-aos="fade-up"
          data-aos-delay="300"
          sx={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem', mt: 2 }}
        >
          * A one-time installation fee applies, refundable upon service termination.
        </Typography>
      </Box>
    </Box>
  )
}

export default Pricing
